import { useState, useRef, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SubPageHeader from '@/components/layout/SubPageHeader';
import SubPageShell from '@/components/layout/SubPageShell';
import SubPageSection from '@/components/layout/SubPageSection';
import SubPageSurface from '@/components/layout/SubPageSurface';
import { InfoField, InfoSidebarSectionTitle } from '@/components/base/InfoField';
import SectionHeading from '@/components/base/SectionHeading';

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

type ClientType = 'individual' | 'corporate';

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [clientType, setClientType] = useState<ClientType>('individual');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [submitNotice, setSubmitNotice] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg(null);
    setSubmitNotice(null);
    if (charCount > 500) return;
    const url = `${import.meta.env.BASE_URL}contact.php`;
    setSubmitting(true);
    const form = e.currentTarget;
    const fd = new FormData(form);
    fd.set("client_type", clientType === "individual" ? "個人" : "法人・団体");
    if (clientType === "individual") {
      fd.delete("company");
    }
    try {
      const res = await fetch(url, {
        method: "POST",
        body: fd,
      });
      const raw = await res.text();
      let data: {
        ok?: boolean;
        message?: string;
        warning?: string;
        detail?: string;
        exception?: string;
      } = {};
      try {
        data = raw ? (JSON.parse(raw) as typeof data) : {};
      } catch {
        setErrorMsg(
          res.ok
            ? "サーバーから不正な応答がありました。"
            : `サーバーエラー (${res.status})。PHPのエラーでHTMLが返っている可能性があります。開発者ツールの Network → Response の先頭を確認してください。`
        );
        if (import.meta.env.DEV) {
          console.error("[contact] Non-JSON response:", raw.slice(0, 800));
        }
        return;
      }
      if (!res.ok || !data.ok) {
        const hint =
          data.message ||
          data.detail ||
          (data.exception ? `(${data.exception})` : null) ||
          "送信に失敗しました。時間をおいて再度お試しください。";
        setErrorMsg(hint);
        return;
      }
      if (data.warning) {
        setSubmitNotice(data.warning);
      }
      setSubmitted(true);
    } catch {
      setErrorMsg("通信エラーが発生しました。ネットワークをご確認ください。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SubPageShell>
      <Navbar />

      <SubPageHeader>
          <SectionHeading label="CONTACT" title="お問い合わせ" as="h1" tone="dark" size="page" />
          <p
            className="mt-6 max-w-xl"
            style={{ fontSize: '17px', color: '#FFFFFF', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '2.0' }}
          >
            ご質問・ご相談・お見積りのご依頼など、お気軽にお問い合わせください。
          </p>
      </SubPageHeader>

      {/* Contact Content */}
      <SubPageSection>
          <FadeIn>
            <SectionHeading label="INQUIRY" title="お問い合わせフォーム" tone="dark" className="mb-14" />
          </FadeIn>

          <SubPageSurface className="p-8 md:p-10 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-20">

            {/* Left info */}
            <div className="lg:col-span-2">
              <FadeIn>
                <InfoSidebarSectionTitle>INFO</InfoSidebarSectionTitle>
                <p
                  className="mb-12 text-[17px] leading-[2.1] text-slate-700"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                >
                  担当者より折り返しご連絡いたします。お急ぎの場合はお電話にてお問い合わせください。
                </p>

                <div className="space-y-11">
                  <InfoField label="ADDRESS">
                    <p style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
                      〒039-1114
                      <br />
                      青森県八戸市北白山台5丁目2-5
                      <br />
                      東奥日報社八戸ビル5階
                    </p>
                  </InfoField>

                  <InfoField label="TEL / FAX">
                    <p
                      className="text-[19px] font-medium tabular-nums"
                      style={{ fontFamily: '"Inter", sans-serif', color: '#0f172a' }}
                    >
                      TEL 0178-51-8296
                      <br />
                      FAX 0178-51-8297
                    </p>
                  </InfoField>

                  <InfoField label="E-MAIL">
                    <a
                      href="mailto:cadec@cadec.co.jp"
                      className="inline-block text-[18px] transition-colors duration-200"
                      style={{ fontFamily: '"Inter", sans-serif', color: '#0f172a' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#B48C50'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#0f172a'; }}
                    >
                      cadec@cadec.co.jp
                    </a>
                  </InfoField>

                  <InfoField label="HOURS">
                    <p style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>平日 9:00 〜 18:00</p>
                    <p className="mt-2 text-[14px] leading-relaxed text-slate-600" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
                      土日祝・夏期・冬期休暇を除く
                    </p>
                  </InfoField>
                </div>
              </FadeIn>
            </div>

            {/* Right form */}
            <div className="lg:col-span-3">
              <FadeIn delay={150}>
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-24 text-center">
                    <div
                      className="w-12 h-12 flex items-center justify-center mb-8"
                      style={{ border: '1px solid #111111' }}
                    >
                      <i className="ri-check-line" style={{ fontSize: '18px', color: '#111111' }} />
                    </div>
                    <h3
                      className="font-bold mb-3"
                      style={{ fontSize: '18px', color: '#111111', fontFamily: '"Noto Sans JP", sans-serif' }}
                    >
                      送信が完了しました
                    </h3>
                    <p
                      style={{ fontSize: '15px', color: '#6B7280', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '2.0' }}
                    >
                      お問い合わせありがとうございます。<br />
                      担当者よりご連絡いたします。
                    </p>
                    {submitNotice ? (
                      <p
                        className="mt-6 max-w-md mx-auto text-left"
                        style={{ fontSize: '16px', color: '#B45309', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '1.8' }}
                        role="status"
                      >
                        {submitNotice}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-7">

                    {/* 区分 */}
                    <div>
                      <label className="mb-3 flex flex-wrap items-baseline gap-2" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
                        <span className="text-[12px] font-bold tracking-wide text-slate-700">お客様区分</span>
                        <span className="text-[11px] font-semibold text-white px-2 py-0.5" style={{ background: '#111', fontFamily: '"Inter", sans-serif', letterSpacing: '0.08em' }}>必須</span>
                      </label>
                      <div className="flex gap-0" style={{ border: '1px solid #E5E5E0' }}>
                        <button
                          type="button"
                          onClick={() => setClientType('individual')}
                          className="flex-1 py-3 text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
                          style={{
                            background: clientType === 'individual' ? '#111111' : '#FAFAF8',
                            color: clientType === 'individual' ? '#FFFFFF' : '#9CA3AF',
                            fontFamily: '"Noto Sans JP", sans-serif',
                            fontSize: '15px',
                            borderRight: '1px solid #E5E5E0',
                          }}
                        >
                          個人
                        </button>
                        <button
                          type="button"
                          onClick={() => setClientType('corporate')}
                          className="flex-1 py-3 text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
                          style={{
                            background: clientType === 'corporate' ? '#111111' : '#FAFAF8',
                            color: clientType === 'corporate' ? '#FFFFFF' : '#9CA3AF',
                            fontFamily: '"Noto Sans JP", sans-serif',
                            fontSize: '15px',
                          }}
                        >
                          法人・団体
                        </button>
                      </div>
                    </div>

                    {/* 氏名 */}
                    <div>
                      <label className="mb-2 flex flex-wrap items-baseline gap-2" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
                        <span className="text-[12px] font-bold tracking-wide text-slate-700">お名前</span>
                        <span className="text-[11px] font-semibold text-white px-2 py-0.5" style={{ background: '#111', fontFamily: '"Inter", sans-serif', letterSpacing: '0.08em' }}>必須</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="山田 太郎"
                        className="w-full px-4 py-3 text-sm outline-none transition-all duration-200"
                        style={{
                          background: '#FAFAF8',
                          border: '1px solid #E5E5E0',
                          fontFamily: '"Noto Sans JP", sans-serif',
                          color: '#1A1A1A',
                          fontSize: '16px',
                        }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = '#111111'; e.currentTarget.style.background = '#FFFFFF'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E5E0'; e.currentTarget.style.background = '#FAFAF8'; }}
                      />
                    </div>

                    {/* 会社名（法人のみ） */}
                    {clientType === 'corporate' && (
                      <div>
                        <label className="mb-2 flex flex-wrap items-baseline gap-2" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
                          <span className="text-[12px] font-bold tracking-wide text-slate-700">会社名・団体名</span>
                          <span className="text-[11px] font-semibold text-white px-2 py-0.5" style={{ background: '#111', fontFamily: '"Inter", sans-serif', letterSpacing: '0.08em' }}>必須</span>
                        </label>
                        <input
                          type="text"
                          name="company"
                          required={clientType === 'corporate'}
                          placeholder="株式会社〇〇"
                          className="w-full px-4 py-3 text-sm outline-none transition-all duration-200"
                          style={{
                            background: '#FAFAF8',
                            border: '1px solid #E5E5E0',
                            fontFamily: '"Noto Sans JP", sans-serif',
                            color: '#1A1A1A',
                            fontSize: '16px',
                          }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = '#111111'; e.currentTarget.style.background = '#FFFFFF'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E5E0'; e.currentTarget.style.background = '#FAFAF8'; }}
                        />
                      </div>
                    )}

                    {/* メールアドレス */}
                    <div>
                      <label className="mb-2 flex flex-wrap items-baseline gap-2" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
                        <span className="text-[12px] font-bold tracking-wide text-slate-700">メールアドレス</span>
                        <span className="text-[11px] font-semibold text-white px-2 py-0.5" style={{ background: '#111', fontFamily: '"Inter", sans-serif', letterSpacing: '0.08em' }}>必須</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="info@example.com"
                        className="w-full px-4 py-3 text-sm outline-none transition-all duration-200"
                        style={{
                          background: '#FAFAF8',
                          border: '1px solid #E5E5E0',
                          fontFamily: '"Inter", sans-serif',
                          color: '#1A1A1A',
                          fontSize: '16px',
                        }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = '#111111'; e.currentTarget.style.background = '#FFFFFF'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E5E0'; e.currentTarget.style.background = '#FAFAF8'; }}
                      />
                    </div>

                    {/* お問い合わせ内容 */}
                    <div>
                      <label className="mb-2 flex flex-wrap items-baseline gap-2" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
                        <span className="text-[12px] font-bold tracking-wide text-slate-700">お問い合わせの内容</span>
                        <span className="text-[11px] font-semibold text-white px-2 py-0.5" style={{ background: '#111', fontFamily: '"Inter", sans-serif', letterSpacing: '0.08em' }}>必須</span>
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={7}
                        maxLength={500}
                        placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                        className="w-full px-4 py-3 text-sm outline-none transition-all duration-200 resize-none"
                        style={{
                          background: '#FAFAF8',
                          border: '1px solid #E5E5E0',
                          fontFamily: '"Noto Sans JP", sans-serif',
                          color: '#1A1A1A',
                          fontSize: '16px',
                        }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = '#111111'; e.currentTarget.style.background = '#FFFFFF'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E5E0'; e.currentTarget.style.background = '#FAFAF8'; }}
                        onChange={(e) => setCharCount(e.target.value.length)}
                      />
                      <div className="flex justify-between mt-1.5">
                        <span style={{ fontSize: '15px', color: '#CCCCCC', fontFamily: '"Noto Sans JP", sans-serif' }}>
                          500文字以内でご記入ください
                        </span>
                        <span style={{ fontSize: '15px', color: charCount >= 500 ? '#EF4444' : '#AAAAAA', fontFamily: '"Inter", sans-serif' }}>
                          {charCount} / 500
                        </span>
                      </div>
                    </div>

                    {errorMsg ? (
                      <p
                        className="text-sm"
                        style={{ color: "#DC2626", fontFamily: '"Noto Sans JP", sans-serif' }}
                        role="alert"
                      >
                        {errorMsg}
                      </p>
                    ) : null}

                    <button
                      type="submit"
                      disabled={submitting || charCount > 500}
                      className="w-full py-4 text-sm font-medium tracking-widest transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-3"
                      style={{
                        background: submitting ? '#9CA3AF' : '#111111',
                        color: '#FFFFFF',
                        fontFamily: '"Inter", sans-serif',
                        letterSpacing: '0.2em',
                        fontSize: '16px',
                      }}
                      onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = '#333333'; }}
                      onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.background = '#111111'; }}
                    >
                      {submitting ? (
                        <>
                          <i className="ri-loader-4-line" style={{ fontSize: '16px', animation: 'spin 1s linear infinite' }} />
                          SENDING...
                        </>
                      ) : (
                        <>
                          SEND MESSAGE
                          <i className="ri-arrow-right-line" style={{ fontSize: '16px' }} />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </FadeIn>
            </div>
          </div>
          </SubPageSurface>
      </SubPageSection>

      <Footer />
    </SubPageShell>
  );
}
